import { IListItem, IGeneric } from '@medlogic/shared/shared-interfaces';
import { IGenericState } from '@medlogic/shared/shared-interfaces';
import { createSelector } from '@ngrx/store';


export const selectAllGenerics = (state: IGenericState) => state?.allGenerics ? state.allGenerics : undefined;
export const selectedGeneric = (state: IGenericState) => state;
export const selectOpenedFromGenericList = (state: IGenericState) => state?.openedFromGenericList ?? false;
export const selectGenericLoadScope = (state: IGenericState) => state?.loadScope;

export const selectGenericById = (id: number) => createSelector(
  selectAllGenerics,
  allGenerics => {
    if (allGenerics && id) {
      return allGenerics.find(f => f.id && +f.id === +id);
    }
    return null;
  });

// Para que se faça o preenchimento de propriedades padrão
export const selectDefaultFormControl = createSelector(
  selectedGeneric,
  selected => {
    return selected.selectedGeneric ? Object
      .keys(selected.identification)
      .reduce((obj, key) => {
        obj[getId(selected.identification[key])] = selected.selectedGeneric[key];
        return obj;
      }, {}) : null;
  }
);

function getId(vno: any): string {
  return isNaN(vno) ? null : `V_${vno}`;
}

function normalizeText(value: any): string {
  if (value === null || value === undefined) return '';
  return value
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export const selectAllGenericsAsListItems = createSelector(
  selectAllGenerics,
  (allGenerics) => {
    if (allGenerics) {
      return allGenerics;
    }

    return undefined;
  });

export const selectAllGenericsAsListItemsByKeyword = createSelector(
  selectAllGenericsAsListItems,
  (allItems: IListItem<IGeneric>[], props: { keyword: string, filter: { selectedFilter: 'active' | 'inactive' | 'all', residentName: string, activityName: string } }) => {
    if (allItems === undefined) { return undefined; }

    if (allItems?.length > 0) {
      const normalizedKeyword = normalizeText(props?.keyword);
      return allItems.filter(f =>
        f &&
        (normalizedKeyword === '' ||
          normalizeText(f.topLeft).includes(normalizedKeyword) ||
          normalizeText(f.topRight).includes(normalizedKeyword) ||
          normalizeText(f.bottomLeft).includes(normalizedKeyword) ||
          normalizeText(f.bottomRight).includes(normalizedKeyword) ||
          normalizeText(f.ono).includes(normalizedKeyword))
      ).sort((a, b) => {
        if (a && b && a.bottomRight && b.bottomRight) {
          let aSeparatorDateTime = ' ';
          let bSeparatorDateTime = ' ';

          if (a.bottomRight.includes('T')) {
            aSeparatorDateTime = 'T';
          }

          if (b.bottomRight.includes('T')) {
            bSeparatorDateTime = 'T';
          }

          const [aDate, aTime] = a.bottomRight.split(aSeparatorDateTime);
          const [bDate, bTime] = b.bottomRight.split(bSeparatorDateTime);

          const [aDay, aMonth, aYear] = aDate.split('/');
          const [bDay, bMonth, bYear] = bDate.split('/');
          const [aHour, aMinute, aSecond] = aTime ? aTime.split(':') : ['00', '00', '00'];
          const [bHour, bMinute, bSecond] = bTime ? bTime.split(':') : ['00', '00', '00'];

          const byDate = new Date(+bYear, +bMonth - 1, +bDay, +bHour, +bMinute, +bSecond).getTime() - new Date(+aYear, +aMonth - 1, +aDay, +aHour, +aMinute, +aSecond).getTime();

          if (byDate !== 0) return byDate;
        }

        // const nameA = (a?.topLeft || '').toString();
        // const nameB = (b?.topLeft || '').toString();
        // const alpha = nameB.localeCompare(nameA);

        // if (alpha !== 0) return alpha;

        return a && b && a.ono && b.ono ? Number(b.ono) - Number(a.ono) : 0;
      });
    } else {
      return [];
    }
  });

export const selectAllGenericsAsListItemsByKeywordAndFilter = createSelector(
  selectAllGenericsAsListItemsByKeyword,
  (allItems: IListItem<IGeneric>[], props: { keyword: string, filter: { selectedFilter: 'active' | 'inactive' | 'all', residentName: string, activityName: string } }) => {
    if (allItems === undefined) { return undefined; }
    if (allItems?.length > 0) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const parseDdMmYyyyThhMmSsToDate = (value: any): Date | null => {
        if (!value) return null;
        if (value instanceof Date && !isNaN(value.valueOf())) return value;

        const raw = value.toString().replace(/"/g, '').trim();
        if (!raw) return null;

        const split = raw.indexOf('T') >= 0 ? raw.split('T') : raw.split(' ');
        const datePart = split[0];
        const timePart = split[1];
        const dt = datePart.indexOf('/') >= 0 ? datePart.split('/') : datePart.split('-');
        if (dt.length < 3) return null;

        const hr = timePart ? timePart.split(':') : ['00', '00', '00'];

        const dia = Number(dt[0]);
        const mes = Number(dt[1]) - 1;
        const ano = Number(dt[2]);
        const hora = Number(hr[0] ?? '00');
        const minuto = Number(hr[1] ?? '00');
        const segundo = Number(hr[2] ?? '00');

        if (!Number.isFinite(dia) || !Number.isFinite(mes) || !Number.isFinite(ano)) return null;
        return new Date(ano, mes, dia, hora, minuto, segundo);
      };

      return allItems.filter(f => {
        if (!f) return false;
        if (props?.filter) {
          const { selectedFilter, residentName, activityName } = props.filter;

          const typeRaw = f.obj?.type;
          const typeNorm = normalizeText(typeRaw);
          const endDate = parseDdMmYyyyThhMmSsToDate(f.obj?.dtEnd);
          const isEndDateValid = endDate instanceof Date && !isNaN(endDate.valueOf());

          const isPermanent = typeNorm.includes('perman');
          const isTemporary = typeNorm.includes('tempor');

          const isTemporaryActive = isTemporary && (!isEndDateValid || endDate >= today);
          const isTemporaryInactive = isTemporary && isEndDateValid && endDate < today;

          const isTypeFlagActiveInactive = typeNorm === 'active' || typeNorm === 'inactive';

          if (selectedFilter && selectedFilter !== 'all') {
            if (isTypeFlagActiveInactive) {
              if (typeNorm !== selectedFilter) return false;
            } else if (isPermanent || isTemporary) {
              if (selectedFilter === 'active' && !(isPermanent || isTemporaryActive)) return false;
              if (selectedFilter === 'inactive' && !isTemporaryInactive) return false;
            } else {
              if (typeRaw && typeRaw !== selectedFilter) return false;
            }
          }
          if (residentName && f.bottomLeft !== residentName) return false;
          if (activityName && f.identification1 !== activityName) return false;
        }
        return true;
      });
    } else {
      return [];
    }
  });
